import type { GetMessagesQueryVariables } from '#src/graphql/generated';

import { NetworkStatus } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import {
  MessageList,
  type ChatMessageQueryOptions,
} from '#src/components/message-list';
import { toaster } from '#src/components/ui/toaster';
import { CHAT_MESSAGE_QUERY_PROCESSOR_ID } from '#src/constants/processors';
import { mergeCreateMessageIntoGetMessagesCache } from '#src/graphql/cache/merge-create-message-into-get-messages';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useScrollMessageListToEnd } from '#src/hooks/use-scroll-message-list-to-end';
import { useThreadFocusSearchParams } from '#src/hooks/use-thread-focus-search-params';
import { usePageFooter } from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';
import {
  findThreadRootForMessage,
  resolveFocusParamToRootId,
} from '#src/utils/message-thread-focus';
import { persistProcessorFailureAsThreadReply } from '#src/utils/persist-processor-failure-message';

export function Dashboard() {
  const { footerRef } = usePageFooter();
  const user = useCurrentUser();
  const apolloClient = useApolloClient();

  const {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
  } = useThreadFocusSearchParams();

  const getMessagesVariables = useMemo<GetMessagesQueryVariables>(
    () => ({}),
    [],
  );

  const [messageText, setMessageText] = useState('');

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);
  const [executeChatMessageQuery] = useMutation(EXECUTE_PROCESSOR);

  const [chatMessageQueryLoadingId, setChatMessageQueryLoadingId] = useState<
    string | null
  >(null);

  const {
    data: messagesData,
    error: messagesError,
    networkStatus: messagesNetworkStatus,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables: getMessagesVariables,
    fetchPolicy: 'cache-and-network',
  });

  /**
   * `useQuery` does not expose a dedicated "initial only" flag; `networkStatus.loading`
   * is only true before the first result. Refetches (e.g. after AI actions or delete)
   * use `refetch` status and do not clear `messagesData`, so the list stays visible.
   * New messages from send are merged via the mutation `update` (no refetch).
   */
  const isMessagesInitialLoad =
    messagesNetworkStatus === NetworkStatus.loading && messagesData == null;

  const sortedMessages = useMemo(
    () =>
      [...(messagesData?.messages || [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [messagesData?.messages],
  );

  const focusedRootId = useMemo(
    () => resolveFocusParamToRootId(rawFocusMessageId, sortedMessages),
    [rawFocusMessageId, sortedMessages],
  );

  const displayMessages = useMemo(
    () =>
      focusedRootId
        ? sortedMessages.filter((m) => m.id === focusedRootId)
        : sortedMessages,
    [sortedMessages, focusedRootId],
  );

  useEffect(() => {
    if (isMessagesInitialLoad) return;
    if (rawFocusMessageId && !focusedRootId) {
      replaceStripFocusParam();
    }
  }, [
    isMessagesInitialLoad,
    rawFocusMessageId,
    focusedRootId,
    replaceStripFocusParam,
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  useScrollMessageListToEnd(messagesEndRef, displayMessages);

  const handleRunChatMessageQuery = useCallback(
    async (
      messageId: string,
      query: string,
      options?: ChatMessageQueryOptions,
    ) => {
      setChatMessageQueryLoadingId(messageId);
      try {
        const variables: Record<string, unknown> = { messageId, query };
        if (options?.iterative) {
          variables.iterative = true;
        }
        const { data } = await executeChatMessageQuery({
          variables: {
            input: {
              processorId: CHAT_MESSAGE_QUERY_PROCESSOR_ID,
              variables,
            },
          },
        });
        const ep = data?.executeProcessor;
        const inner = ep?.result as
          | {
              success?: boolean;
              error?: string;
              documentUpdated?: boolean;
            }
          | undefined;
        const instanceId = ep?.instanceId?.trim();
        const root = findThreadRootForMessage(sortedMessages, messageId);

        if (!ep?.success) {
          if (root) {
            const persist = await persistProcessorFailureAsThreadReply(
              apolloClient,
              root.id,
              ep?.error ?? 'Processor failed',
              instanceId,
            );
            if (!persist.ok) {
              toaster.create({
                title: 'Could not save error to thread',
                description: persist.error,
                type: 'warning',
              });
            }
            await refetchMessages();
          } else {
            toaster.create({
              title: 'Could not run AI action',
              description: ep?.error ?? 'Processor failed',
              type: 'error',
            });
          }
          return;
        }
        if (inner?.success === false) {
          if (root) {
            const persist = await persistProcessorFailureAsThreadReply(
              apolloClient,
              root.id,
              inner.error ?? 'Request failed',
              instanceId,
            );
            if (!persist.ok) {
              toaster.create({
                title: 'Could not save error to thread',
                description: persist.error,
                type: 'warning',
              });
            }
            await refetchMessages();
          } else {
            toaster.create({
              title: 'Could not run AI action',
              description: inner.error ?? 'Request failed',
              type: 'error',
            });
          }
          return;
        }
        await refetchMessages();
        if (inner?.documentUpdated === true) {
          toaster.create({
            title: 'Document updated',
            description:
              'Changes were saved and a reply was added to this thread.',
            type: 'success',
          });
        } else {
          toaster.create({
            title: 'AI note added',
            description: 'A reply was added to this thread.',
            type: 'success',
          });
        }
      } catch (error) {
        const root = findThreadRootForMessage(sortedMessages, messageId);
        const description =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred';
        if (root) {
          const persist = await persistProcessorFailureAsThreadReply(
            apolloClient,
            root.id,
            description,
            undefined,
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to thread',
              description: persist.error,
              type: 'warning',
            });
          }
          await refetchMessages();
        } else {
          toaster.create({
            title: 'Could not run AI action',
            description,
            type: 'error',
          });
        }
      } finally {
        setChatMessageQueryLoadingId(null);
      }
    },
    [apolloClient, executeChatMessageQuery, refetchMessages, sortedMessages],
  );

  const handleSendMessage = async () => {
    const value = messageText.trim();
    if (!value) return;
    try {
      await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: focusedRootId ?? undefined,
          },
        },
        update(cache, { data }) {
          if (data?.createMessage) {
            mergeCreateMessageIntoGetMessagesCache(
              cache,
              data.createMessage,
              getMessagesVariables,
            );
          }
        },
      });
      setMessageText('');
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const footerContent = user.email ? (
    <VStack gap={2} align="stretch" width="100%">
      {focusedRootId && (
        <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
          <Text>Replying in thread</Text>
          <Button size="xs" variant="ghost" onClick={() => collapseThread()}>
            Cancel
          </Button>
        </HStack>
      )}
      <ChatComposerRow
        value={messageText}
        onChange={setMessageText}
        onSubmit={handleSendMessage}
        placeholder={focusedRootId ? 'Type a reply…' : 'Type a message…'}
        loading={isSendingMessage}
      />
    </VStack>
  ) : null;

  return (
    <>
      {/* Render message input into footer portal */}
      {footerRef.current && createPortal(footerContent, footerRef.current)}

      <MessageList
        messages={displayMessages}
        loading={isMessagesInitialLoad}
        error={messagesError ?? undefined}
        emptyMessage="No messages found."
        focusedRootId={focusedRootId}
        onExpandThread={expandThread}
        messagesEndRef={messagesEndRef}
        onRunChatMessageQuery={handleRunChatMessageQuery}
        chatMessageQueryLoadingId={chatMessageQueryLoadingId}
        alignMessagesToBottom
        onMessageDeleted={() => void refetchMessages()}
      />
    </>
  );
}
